import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { getEducation } from '../services/data';
import type { EducationItem } from '../types';
import workshop from '../assets/climate-workshop.webp';
import hotCity from '../assets/hot-city.webp';
import activePark from '../assets/active-park.webp';

const iconByType: Record<string, string> = { sun: '☀', air: '≋', tree: '♧', bike: '🚲' };

const lessonDetails: Record<string, {
  color: string;
  image: string;
  facts: string[];
  quiz: { question: string; options: string[]; answer: number; explanation: string };
}> = {
  'urban-heat': {
    color: 'orange',
    image: hotCity,
    facts: [
      'Permukaan beton dan aspal menyerap panas lebih lama daripada tanah dan vegetasi.',
      'Pepohonan membantu melalui naungan dan proses penguapan air dari daun.',
      'Waktu, intensitas aktivitas, dan pilihan rute dapat mengurangi paparan panas.',
    ],
    quiz: {
      question: 'Pilihan paling tepat saat suhu terasa sangat tinggi adalah…',
      options: ['Menambah intensitas lari', 'Memilih jam lebih sejuk dan rute teduh', 'Mengabaikan kondisi karena jarak pendek'],
      answer: 1,
      explanation: 'Menggeser waktu dan memilih rute teduh adalah bentuk adaptasi sederhana terhadap panas perkotaan.',
    },
  },
  'air-quality': {
    color: 'blue',
    image: workshop,
    facts: [
      'Kualitas udara dapat berubah menurut lokasi, waktu, cuaca, dan kepadatan lalu lintas.',
      'Aktivitas berat meningkatkan volume udara yang masuk ke paru-paru.',
      'Kelompok sensitif perlu lebih berhati-hati saat kualitas udara memburuk.',
    ],
    quiz: {
      question: 'Saat kualitas udara kurang baik, tindakan yang lebih bijak adalah…',
      options: ['Memilih aktivitas ringan atau waktu lain', 'Tetap berolahraga berat', 'Hanya melihat suhu'],
      answer: 0,
      explanation: 'Menurunkan intensitas atau memilih waktu lain membantu mengurangi paparan saat udara kurang baik.',
    },
  },
  'green-space': {
    color: 'green',
    image: activePark,
    facts: [
      'Ruang hijau memberi naungan dan membantu menurunkan suhu permukaan.',
      'Taman juga memiliki manfaat sosial, kesehatan mental, dan ruang belajar warga.',
      'Kualitas ruang hijau dipengaruhi oleh akses, perawatan, vegetasi, dan fasilitas.',
    ],
    quiz: {
      question: 'Manfaat ruang hijau bagi kenyamanan kota adalah…',
      options: ['Hanya untuk dekorasi', 'Memberi teduh dan ruang aktivitas warga', 'Meningkatkan panas permukaan'],
      answer: 1,
      explanation: 'Ruang hijau memberi teduh, membantu kenyamanan termal, dan menjadi ruang aktivitas sosial.',
    },
  },
  'low-carbon': {
    color: 'teal',
    image: activePark,
    facts: [
      'Perjalanan pendek berpotensi diganti dengan berjalan atau bersepeda.',
      'Transportasi publik dapat mengurangi kebutuhan kendaraan pribadi per orang.',
      'Mobilitas aktif juga menambah aktivitas fisik harian.',
    ],
    quiz: {
      question: 'Contoh pilihan mobilitas rendah emisi adalah…',
      options: ['Berjalan untuk perjalanan dekat', 'Menyalakan kendaraan saat berhenti lama', 'Menggunakan kendaraan sendiri untuk semua perjalanan'],
      answer: 0,
      explanation: 'Berjalan untuk perjalanan dekat mengurangi emisi sekaligus memberi manfaat aktivitas fisik.',
    },
  },
};

function loadStoredIds(key: string): string[] {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) as string[] : [];
  } catch {
    return [];
  }
}

export function LearnPage() {
  const [items, setItems] = useState<EducationItem[]>([]);
  const [selected, setSelected] = useState<EducationItem | null>(null);
  const [category, setCategory] = useState('Semua');
  const [query, setQuery] = useState('');
  const [completed, setCompleted] = useState<string[]>(() => loadStoredIds('jc-learning-completed'));
  const [saved, setSaved] = useState<string[]>(() => loadStoredIds('jc-learning-saved'));
  const [quizAnswer, setQuizAnswer] = useState<number | null>(null);
  const [showSavedOnly, setShowSavedOnly] = useState(false);

  useEffect(() => {
    void getEducation().then((data) => {
      setItems(data);
      setSelected(data[0] ?? null);
    });
  }, []);

  useEffect(() => {
    localStorage.setItem('jc-learning-completed', JSON.stringify(completed));
  }, [completed]);

  useEffect(() => {
    localStorage.setItem('jc-learning-saved', JSON.stringify(saved));
  }, [saved]);

  const categories = useMemo(() => ['Semua', ...Array.from(new Set(items.map((item) => item.category)))], [items]);
  const visibleItems = useMemo(() => items.filter((item) => {
    const categoryMatch = category === 'Semua' || item.category === category;
    const normalized = `${item.title} ${item.summary} ${item.category}`.toLowerCase();
    const savedMatch = !showSavedOnly || saved.includes(item.id);
    return categoryMatch && savedMatch && normalized.includes(query.toLowerCase());
  }), [items, category, query, saved, showSavedOnly]);

  const progress = items.length ? Math.round((completed.length / items.length) * 100) : 0;
  const selectedDetail = selected ? lessonDetails[selected.id] : undefined;
  const quiz = selectedDetail?.quiz;

  function selectLesson(item: EducationItem) {
    setSelected(item);
    setQuizAnswer(null);
  }

  function toggleCompleted(id: string) {
    setCompleted((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  }

  function toggleSaved(id: string) {
    setSaved((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  }

  function continueLearning() {
    const next = items.find((item) => !completed.includes(item.id)) ?? items[0];
    if (!next) return;
    selectLesson(next);
    window.requestAnimationFrame(() => document.getElementById('lesson-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  return (
    <div className="app-page learn-page learn-page--interactive">
      <header className="app-page__header">
        <div>
          <span className="eyebrow">Climate Learning Hub</span>
          <h1>Belajar isu iklim melalui kondisi kota yang dekat dengan kehidupan sehari-hari.</h1>
        </div>
        <div className="learning-progress-card" aria-label={`Progres belajar ${progress}%`}>
          <div className="learning-progress-card__ring" style={{ '--progress': `${progress * 3.6}deg` } as CSSProperties}>
            <strong>{progress}%</strong>
          </div>
          <div><strong>Progres belajar</strong><span>{completed.length} dari {items.length} materi dipahami</span></div>
        </div>
      </header>

      <section className="learning-overview">
        <article className="learning-intro-card">
          <img src={workshop} alt="Kelas literasi iklim bersama warga" />
          <div className="learning-intro-card__overlay" />
          <div className="learning-intro-card__content">
            <span className="eyebrow eyebrow--light">Belajar singkat dan dapat ditindaklanjuti</span>
            <h2>Pahami satu isu, jawab satu kuis, lalu pilih satu aksi yang bisa dilakukan hari ini.</h2>
            <p>Materi dirancang hanya 2–3 menit agar data lingkungan terasa dekat dengan keputusan warga.</p>
            <div className="learning-intro-card__actions">
              <button className="button button--light button--small" type="button" onClick={continueLearning}>Lanjutkan belajar</button>
              <button className={`button button--small ${showSavedOnly ? 'button--joined' : 'learning-button--glass'}`} type="button" onClick={() => setShowSavedOnly((value) => !value)}>
                {showSavedOnly ? 'Tampilkan semua' : `Materi tersimpan (${saved.length})`}
              </button>
            </div>
          </div>
        </article>

        <div className="learning-summary-grid">
          <button type="button" className="learning-summary-card" onClick={continueLearning}>
            <span>◫</span><div><strong>{items.length || 4}</strong><small>Materi tersedia</small></div><b>Mulai →</b>
          </button>
          <button type="button" className="learning-summary-card" onClick={() => setShowSavedOnly((value) => !value)}>
            <span>★</span><div><strong>{saved.length}</strong><small>Materi tersimpan</small></div><b>Lihat →</b>
          </button>
          <div className="learning-summary-card learning-summary-card--progress">
            <span>✓</span><div><strong>{progress}%</strong><small>Progres belajar</small></div><b>{completed.length}/{items.length}</b>
          </div>
        </div>
      </section>

      <section className="learning-toolbar">
        <label className="learning-search">
          <span>⌕</span>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cari panas, udara, ruang hijau, atau mobilitas..." />
        </label>
        <div className="filter-chips" aria-label="Filter kategori edukasi">
          {categories.map((item) => (
            <button key={item} type="button" className={category === item ? 'is-active' : ''} onClick={() => setCategory(item)}>{item}</button>
          ))}
        </div>
      </section>

      <div className="learn-layout learn-layout--interactive">
        <section className="learning-library">
          <div className="content-section__heading">
            <div><span className="eyebrow">Perpustakaan mini</span><h2>Pilih materi yang ingin dipahami</h2></div>
            <span className="learning-count">{visibleItems.length} materi</span>
          </div>

          <div className="education-card-list education-card-list--interactive">
            {visibleItems.map((item) => {
              const detail = lessonDetails[item.id];
              const isCompleted = completed.includes(item.id);
              const isSaved = saved.includes(item.id);
              return (
                <article className={`education-card education-card--rich ${selected?.id === item.id ? 'is-active' : ''}`} key={item.id}>
                  <button className="education-card__main" type="button" onClick={() => selectLesson(item)}>
                    <div className={`education-card__visual education-card__visual--${detail?.color ?? 'green'}`}>
                      <img src={detail?.image ?? workshop} alt="" />
                      <span>{iconByType[item.icon] ?? '◫'}</span>
                    </div>
                    <div className="education-card__body">
                      <small>{item.category} · {item.duration}</small>
                      <strong>{item.title}</strong>
                      <p>{item.summary}</p>
                      <div className="education-card__meta">
                        <span>{isCompleted ? '✓ Sudah dipahami' : 'Belum selesai'}</span>
                        <span>{detail?.facts.length ?? 3} insight</span>
                      </div>
                    </div>
                  </button>
                  <button className={`education-save-button ${isSaved ? 'is-saved' : ''}`} type="button" onClick={() => toggleSaved(item.id)} aria-label={isSaved ? 'Hapus dari tersimpan' : 'Simpan materi'}>{isSaved ? '★' : '☆'}</button>
                </article>
              );
            })}
          </div>
        </section>

        <aside id="lesson-detail" className="lesson-panel lesson-panel--interactive">
          {selected && selectedDetail && quiz ? (
            <>
              <div className="lesson-panel__cover">
                <img src={selectedDetail.image} alt="" />
                <div className={`lesson-panel__icon lesson-panel__icon--${selectedDetail.color}`}>{iconByType[selected.icon]}</div>
              </div>
              <div className="lesson-panel__heading">
                <div><span className="eyebrow">{selected.category}</span><h2>{selected.title}</h2></div>
                <button className={`education-save-button ${saved.includes(selected.id) ? 'is-saved' : ''}`} type="button" onClick={() => toggleSaved(selected.id)}>{saved.includes(selected.id) ? '★' : '☆'}</button>
              </div>
              <p className="lesson-panel__summary">{selected.summary}</p>

              <section className="lesson-section">
                <h3>Hal penting yang perlu dipahami</h3>
                <ul className="lesson-fact-list">
                  {selectedDetail.facts.map((fact) => <li key={fact}>{fact}</li>)}
                </ul>
              </section>

              <section className="lesson-section lesson-section--action">
                <span>Aksi hari ini</span>
                <h3>{selected.action}</h3>
              </section>

              <section className="lesson-quiz">
                <span className="eyebrow">Kuis cepat</span>
                <h3>{quiz.question}</h3>
                <div className="quiz-option-list">
                  {quiz.options.map((option, index) => (
                    <button
                      key={option}
                      type="button"
                      className={`quiz-option ${quizAnswer === index ? 'is-selected' : ''} ${quizAnswer !== null && index === quiz.answer ? 'is-correct' : ''}`}
                      onClick={() => setQuizAnswer(index)}
                    >
                      <span>{String.fromCharCode(65 + index)}</span>{option}
                    </button>
                  ))}
                </div>
                {quizAnswer !== null && (
                  <div className={`quiz-feedback ${quizAnswer === quiz.answer ? 'is-correct' : 'is-wrong'}`}>
                    <strong>{quizAnswer === quiz.answer ? 'Jawaban tepat' : 'Coba perhatikan kembali'}</strong>
                    <p>{quiz.explanation}</p>
                  </div>
                )}
              </section>

              <button className={`button button--full ${completed.includes(selected.id) ? 'button--ghost' : 'button--primary'}`} type="button" onClick={() => toggleCompleted(selected.id)}>
                {completed.includes(selected.id) ? 'Batalkan status dipahami' : 'Tandai sudah dipahami'}
              </button>
            </>
          ) : <p>Memuat materi edukasi...</p>}
        </aside>
      </div>
    </div>
  );
}
